import { useEffect, useState } from 'react';
import type { UpdateInvoiceInput } from './useUpdateInvoice';

export type InvoiceUpdateJobKind = 'submit' | 'refresh' | 'nex_update';

export interface TrackedInvoiceUpdate {
    mode: 'job' | 'completion';
    jobId?: string;
    invoiceId: string;
    kind: InvoiceUpdateJobKind;
    invoiceNumber?: string;
    input?: UpdateInvoiceInput;
    /** Form fields this update had saved when it was tracked. */
    savedFields?: UpdateInvoiceInput['updatedFields'];
    /** updateCompletedAt on the invoice before a gateway timeout. */
    completedAtBefore?: string | Date | null;
    startedAt: number;
}

export interface FinishedInvoiceUpdate {
    invoiceId: string;
    status: 'succeeded' | 'failed' | 'unknown';
    kind: InvoiceUpdateJobKind;
    input?: UpdateInvoiceInput;
    savedFields?: UpdateInvoiceInput['updatedFields'];
    invoiceStatus?: string;
    nexRefreshWarning?: { title?: string; detail?: string; httpStatus?: number };
    errorMessage?: string;
}

let jobs: TrackedInvoiceUpdate[] = [];
const changeListeners = new Set<() => void>();
const finishListeners = new Set<(result: FinishedInvoiceUpdate) => void>();

function emitChange() {
    changeListeners.forEach((listener) => listener());
}

export function trackInvoiceUpdate(job: Omit<TrackedInvoiceUpdate, 'startedAt'> & { startedAt?: number }) {
    const next: TrackedInvoiceUpdate = { ...job, startedAt: job.startedAt ?? Date.now() };
    jobs = [
        ...jobs.filter((existing) =>
            next.jobId
                ? existing.jobId !== next.jobId
                : !(existing.invoiceId === next.invoiceId && existing.mode === next.mode),
        ),
        next,
    ];
    emitChange();
}

export function listInvoiceUpdates(): TrackedInvoiceUpdate[] {
    return jobs;
}

export function isInvoiceUpdatePending(invoiceId: string | undefined): boolean {
    if (!invoiceId) return false;
    return jobs.some((job) => job.invoiceId === invoiceId);
}

export function finishInvoiceUpdate(result: FinishedInvoiceUpdate, match: { jobId?: string; invoiceId: string; mode: TrackedInvoiceUpdate['mode'] }) {
    jobs = jobs.filter((job) => {
        if (match.jobId) return job.jobId !== match.jobId;
        return !(job.invoiceId === match.invoiceId && job.mode === match.mode);
    });
    emitChange();
    finishListeners.forEach((listener) => listener(result));
}

export function subscribeInvoiceUpdateJobs(listener: () => void): () => void {
    changeListeners.add(listener);
    return () => changeListeners.delete(listener);
}

export function subscribeInvoiceUpdateFinished(listener: (result: FinishedInvoiceUpdate) => void): () => void {
    finishListeners.add(listener);
    return () => finishListeners.delete(listener);
}

export function useInvoiceUpdateJobPending(invoiceId: string | undefined): boolean {
    const [pending, setPending] = useState(() => isInvoiceUpdatePending(invoiceId));
    useEffect(() => {
        setPending(isInvoiceUpdatePending(invoiceId));
        return subscribeInvoiceUpdateJobs(() => setPending(isInvoiceUpdatePending(invoiceId)));
    }, [invoiceId]);
    return pending;
}
